import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  Video,
  Users,
  CheckCircle2,
  Sparkles,
  Loader2,
  Check,
  X,
  ChevronDown,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import StickyCTA from "@/components/StickyCTA";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getRefCode } from "@/lib/referral";
import { toast } from "sonner";
import useSeo from "@/hooks/useSeo";

const PACKAGE_ID = "menopause_invitation_live";
const PRICE_LABEL = "$297 AUD";

// ─── Co-Creators Circle availability toggle ──────────────────────────────────
// Flip to `false` to reopen. When true, the Co-Creators tile shows a "Sold out"
// badge, the CTA is disabled, and the consent modal will not open — so no
// accidental Stripe checkouts can be triggered from the sales page.
const CO_CREATORS_SOLD_OUT = true;

// ─── FULL PROGRAM availability toggle ────────────────────────────────────────
// When true, the entire August 2026 cohort is closed: hero shows a big
// "Sold Out — cohort closed" banner, the $297 Live CTA is disabled, and
// the sticky CTA switches to a waitlist prompt. Backend also blocks the
// checkout endpoint via the TMI_LIVE_SOLD_OUT env var (belt-and-braces).
const TMI_LIVE_SOLD_OUT = true;

const COHORT_DATES = [
  "Mon 3 August 2026 · 7pm AEST",
  "Mon 10 August 2026 · 7pm AEST",
  "Mon 17 August 2026 · 7pm AEST",
  "Mon 24 August 2026 · 7pm AEST",
];

const INCLUSIONS = [
  {
    icon: CheckCircle2,
    title: "The full 6-module digital program",
    body:
      "Access to The Menopause Invitation digital program, so you can work through the core material in your own time.",
  },
  {
    icon: Video,
    title: "Four weekly live Zoom sessions with Pip",
    body:
      "We&apos;ll meet live over four Mondays in August for teaching, reflection, and group coaching.",
  },
  {
    icon: Users,
    title: "Live Q&A and coaching support",
    body:
      "You&apos;ll be able to bring your questions, experiences, and real-life moments into the room.",
  },
  {
    icon: Calendar,
    title: "Teaching recordings after each session",
    body:
      "The teaching portion of each live session is recorded and made available afterwards. The live coaching and Q&A portion is not recorded — so the space can remain more private and honest.",
  },
  {
    icon: Sparkles,
    title: "Practical reflection between sessions",
    body:
      "You&apos;ll be invited to apply the work in your own life — so the program becomes something you can actually use, not just information.",
  },
];

const FOR_YOU = [
  "You know something in you is shifting — and you&apos;re ready to understand it, not just survive it.",
  "You&apos;re done being handed symptom lists and survival tips.",
  "You&apos;ve spent years being capable and reliable — and you&apos;re no longer willing to abandon yourself to keep that up.",
  "You want support that&apos;s grounded, honest, and actually useful.",
  "You&apos;re tired of second-guessing yourself and ready to come back to your own authority.",
  "You want to meet this season with steadiness — not just get through it.",
];

const NOT_FOR_YOU = [
  "You want a quick fix.",
  "You want a purely medical program.",
  "You&apos;re looking for symptom promises.",
  "You want someone else to tell you exactly who to become.",
  "You don&apos;t want to reflect honestly on what&apos;s shifting.",
];

const CURRICULUM = [
  {
    week: "Week 1",
    title: "Seeing the transition clearly",
    body:
      "Understand what menopause may be asking of you beyond the surface symptoms. We&apos;ll begin by making sense of this season as a whole-life transition, not just a hormonal event.",
  },
  {
    week: "Week 2",
    title: "Noticing the patterns",
    body:
      "Identify the emotional, behavioural, relational, and internal loops that may be getting louder right now. We&apos;ll look at what this transition can reveal about old ways of coping, pleasing, performing, over-functioning, or pushing through.",
  },
  {
    week: "Week 3",
    title: "Rebuilding steadiness",
    body:
      "Learn how to meet this stage with more calm, self-respect, and internal grounding. We&apos;ll explore how to respond to what&apos;s changing without abandoning yourself, overriding yourself, or making yourself wrong.",
  },
  {
    week: "Week 4",
    title: "Moving forward with intention",
    body:
      "Clarify what you want to carry forward, what you no longer need, and how to trust yourself in what comes next. We&apos;ll bring the work together so you leave with a clearer sense of how to meet this season in your actual life.",
  },
];

const YOULL_GET = [
  "4 live weekly Zoom sessions with Pip",
  "Access to the full 6-module digital program",
  "Guided teaching and reflection",
  "Live Q&A and coaching support",
  "Practical tools to apply between sessions",
  "A calm, grounded space to explore what this transition means for you",
  "Access to the teaching recordings after each session",
  "A clearer relationship with your own experience",
];

const TESTIMONIALS = [
  {
    body:
      "I thought menopause was just another thing I needed to endure as a woman. How wrong I was! Working with Pip has changed everything. Showing me how to accept my menopause as a power invitation has been life changing.",
    name: "Melissa",
    location: "Sydney",
  },
  {
    body:
      "This was the best spent $297. I've moved from this awful sense of unhappiness, confusion and 'is this it' to creating my own next chapter. I've never done a program like this. Thank you so much Pip.",
    name: "Sarah",
    location: "Queensland",
  },
  {
    body:
      "Who knew my menopause journey could be this good! I wasn't sure what I was signing up to but one of my friends did it and I could see really good shifts in her. Best thing I've done. Pip is amazing. The real deal of coaches.",
    name: "Meaghan",
    location: "Melbourne",
  },
];

const FAQ_ITEMS = [
  {
    q: "Do I need to attend live?",
    a:
      "You&apos;ll get the most from the program if you attend live, because the live sessions give you space for reflection, Q&A, and coaching support. However, if you miss a session, the teaching portion is recorded and made available afterwards.",
  },
  {
    q: "Will the live coaching and Q&A be recorded?",
    a:
      "No. Only the teaching portion of each session is recorded. The live coaching and Q&A is not recorded — so participants can speak more freely and the space can remain more private.",
  },
  {
    q: "What if I&apos;m not sure whether I&apos;m in menopause yet?",
    a:
      "You don&apos;t need to have perfect language for where you are. This program is for women who are in menopause, perimenopause, or somewhere in the messy middle of hormonal and life transition — and who can feel that something is shifting.",
  },
  {
    q: "Is this medical advice?",
    a:
      "No. This program is educational, reflective, and coaching-based. It does not replace medical, psychological, or personalised health advice. Please seek appropriate professional support for any medical or mental-health concerns.",
  },
  {
    q: "Is this only for women with severe symptoms?",
    a:
      "No. This program is not only for women in crisis. It is for women who want to understand what this transition is asking of them — physically, emotionally, mentally, relationally, and personally.",
  },
  {
    q: "Is this the same as the digital program?",
    a:
      "You receive the digital program as part of this offer. The live round adds four weeks of real-time teaching, reflection, Q&A, and coaching with Pip — so you can work with the material in a more supported and personal way.",
  },
];

function CTAButton({ onClick, busy, label = "Join Now", testId, size = "md" }) {
  const sizing =
    size === "lg"
      ? "px-9 md:px-10 py-4 md:py-5 text-base md:text-lg font-bold"
      : "px-8 py-4 text-sm md:text-base font-semibold";
  const shadow =
    size === "lg"
      ? "shadow-[0_18px_45px_-12px_rgba(61,176,122,0.65)] hover:shadow-[0_22px_55px_-12px_rgba(61,176,122,0.75)]"
      : "shadow-[0_10px_30px_-10px_rgba(61,176,122,0.55)]";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2.5 bg-vibrant hover:bg-vibrant-dark disabled:opacity-60 text-cream rounded-full ${sizing} tracking-wide transition-all ${shadow}`}
    >
      {busy ? (
        <>
          <Loader2 size={16} strokeWidth={1.8} className="animate-spin" />
          Opening checkout…
        </>
      ) : (
        <>
          {label}
          <ArrowRight size={size === "lg" ? 20 : 16} strokeWidth={2.2} />
        </>
      )}
    </button>
  );
}

function CTABlock({ onClick, busy, testId }) {
  return (
    <div className="mt-12 md:mt-14 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
      <CTAButton onClick={onClick} busy={busy} testId={testId} />
      <span className="text-xs text-ink-soft/80">
        {PRICE_LABEL} · 4 live calls · lifetime access to the digital program
      </span>
    </div>
  );
}

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-hairline last:border-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 md:py-6 text-left group"
        data-testid={`tmi-live-faq-${index}`}
        aria-expanded={open}
      >
        <span
          className="font-serif-display text-lg md:text-xl text-ink leading-snug group-hover:text-vibrant-dark transition-colors"
          dangerouslySetInnerHTML={{ __html: q }}
        />
        <ChevronDown
          size={20}
          strokeWidth={1.8}
          className={`shrink-0 mt-1 text-vibrant-dark transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p
          className="pb-6 md:pb-7 pr-10 text-ink-soft leading-relaxed text-[0.97rem]"
          dangerouslySetInnerHTML={{ __html: a }}
        />
      )}
    </div>
  );
}

/**
 * 3-pathway tile used on the TMI Live sales page.
 * Front: title, price, short summary, primary CTA, "More details" link.
 * Back:  longer "for you if" lead, includes list, optional footer line, CTA + back link.
 *
 * tone: "sage" | "vibrant" | "gold" — drives the accent palette.
 * featured: bumps the visual weight slightly (used for the headline Live pathway).
 */
function LivePathwayTile({
  tone = "sage",
  eyebrow,
  title,
  price,
  summary,
  buttonLabel,
  isFlipped,
  onFlip,
  onChoose,
  details,
  loading,
  featured = false,
  testIdPrefix,
}) {
  const palette = {
    sage: {
      border: "border-sage/30 hover:border-sage",
      eyebrow: "text-sage",
      price: "text-sage",
      btn: "bg-sage hover:bg-sage-dark text-cream",
    },
    vibrant: {
      border: "border-vibrant/50 hover:border-vibrant",
      eyebrow: "text-vibrant-dark",
      price: "text-vibrant-dark",
      btn: "bg-vibrant hover:bg-vibrant-dark text-cream",
    },
    gold: {
      border: "border-gold/50 hover:border-gold",
      eyebrow: "text-gold-dark",
      price: "text-gold-dark",
      btn: "bg-gold hover:bg-gold-dark text-ink",
    },
  }[tone];

  const featuredClasses = featured
    ? "md:scale-[1.025] md:shadow-[0_22px_55px_-15px_rgba(61,176,122,0.35)] ring-1 ring-vibrant/40"
    : "";

  return (
    <div
      className={`relative rounded-[1.5rem] border-2 ${palette.border} bg-cream p-7 md:p-8 flex flex-col min-h-[30rem] transition-all ${featuredClasses}`}
      data-testid={testIdPrefix}
    >
      {featured && !isFlipped && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-vibrant text-cream text-[0.65rem] tracking-[0.18em] uppercase font-bold px-3.5 py-1 rounded-full whitespace-nowrap">
          Most chosen
        </div>
      )}
      {!isFlipped ? (
        <>
          <div className={`text-[0.7rem] tracking-[0.18em] uppercase font-semibold ${palette.eyebrow} mb-3`}>
            {eyebrow}
          </div>
          <h3 className="font-serif-display text-2xl md:text-[1.75rem] text-ink leading-snug">
            {title}
          </h3>
          <div className={`mt-3 font-serif-display text-3xl md:text-4xl ${palette.price}`}>
            {price}
          </div>
          <p className="mt-5 text-ink-soft leading-relaxed text-[0.97rem] flex-1">
            {summary}
          </p>
          <div className="mt-7 flex flex-col gap-3">
            <button
              type="button"
              onClick={onChoose}
              disabled={loading}
              data-testid={`${testIdPrefix}-cta`}
              className={`inline-flex items-center justify-center gap-2 ${palette.btn} disabled:opacity-60 rounded-full px-5 py-3.5 font-semibold tracking-wide text-sm md:text-[0.95rem] transition-all`}
            >
              {loading ? (
                <>
                  <Loader2 size={15} strokeWidth={1.8} className="animate-spin" />
                  Opening checkout…
                </>
              ) : (
                <>
                  {buttonLabel}
                  <ArrowRight size={15} strokeWidth={2.2} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onFlip}
              className="text-xs font-medium text-ink-soft hover:text-ink underline underline-offset-4 transition self-center"
              data-testid={`${testIdPrefix}-details`}
            >
              What&apos;s included →
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={`text-[0.7rem] tracking-[0.18em] uppercase font-semibold ${palette.eyebrow} mb-3`}>
            {title} — {price}
          </div>
          <p className="text-ink-soft leading-relaxed text-[0.95rem] mb-5">{details.lead}</p>
          <p className="text-ink font-medium text-sm mb-3">Includes:</p>
          <ul className="space-y-2 text-ink-soft text-[0.9rem] leading-relaxed flex-1">
            {details.includes.map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <Check size={13} strokeWidth={2.4} className={`shrink-0 mt-1 ${palette.eyebrow}`} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          {details.footer && (
            <p className="mt-5 font-serif-display italic text-ink text-[0.95rem] leading-snug">
              {details.footer}
            </p>
          )}
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={onChoose}
              disabled={loading}
              data-testid={`${testIdPrefix}-cta-back`}
              className={`inline-flex items-center justify-center gap-2 ${palette.btn} disabled:opacity-60 rounded-full px-5 py-3.5 font-semibold tracking-wide text-sm md:text-[0.95rem] transition-all`}
            >
              {loading ? "Opening checkout…" : buttonLabel}
              {!loading && <ArrowRight size={15} strokeWidth={2.2} />}
            </button>
            <button
              type="button"
              onClick={onFlip}
              className="text-xs font-medium text-ink-soft hover:text-ink underline underline-offset-4 transition self-center"
            >
              ← Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function TheMenopauseInvitationLive() {
  // Placeholder restored — see LivePathwayTile defined above the file's main page
  // component if you scroll up. (Inserted via earlier patch.)
  useSeo({
    title:
      "The Menopause Invitation — Live · August 2026 cohort with Pip Bennett",
    description:
      "A 4-week live program for women in menopause who want to understand what this transition is asking of them, so they can move through it with more clarity, steadiness, and self-trust. Starts 3 August 2026. $297 AUD. 2026 only.",
    canonical: "https://pipbennett.com/the-menopause-invitation-live",
  });

  const [busy, setBusy] = useState(false);
  // Track which package is currently being checked-out so the right tile spinner shows
  const [busyPackage, setBusyPackage] = useState(null);
  // Which of the 3 pathway tiles is currently flipped to show details
  const [pathwayFlipped, setPathwayFlipped] = useState(null); // "digital" | "live" | "research" | null
  // Co-Creators Circle agreement modal — required before $40 checkout
  const [coCreatorsAgreementOpen, setCoCreatorsAgreementOpen] = useState(false);
  const [coCreatorsAgreed, setCoCreatorsAgreed] = useState(false);

  const startCheckout = async () => {
    setBusyPackage(PACKAGE_ID);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: PACKAGE_ID,
        origin_url: window.location.origin,
        agreement_accepted: true,
        ref_code: getRefCode() || undefined,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout could not be started. Please try again.");
        setBusy(false);
        setBusyPackage(null);
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Checkout could not be started. Please try again."
      );
      setBusy(false);
      setBusyPackage(null);
    }
  };

  // Pathway-aware checkout: used by the 3-tile section. Lets a single click on
  // either the Live ($297) tile or the Research ($40) tile open the right Stripe
  // checkout — both grant the same TMI Live cohort access.
  const handlePathwayCheckout = async (packageId) => {
    setBusyPackage(packageId);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
        agreement_accepted: true,
        ref_code: getRefCode() || undefined,
        // Co-Creators Circle requires explicit consent — the modal only calls
        // through to this handler once the visitor has ticked the box, so we
        // can safely stamp `true` here. The backend re-validates this flag for
        // package_id="menopause_invitation_research" and stamps a tamper-proof
        // audit record onto the payment_transactions row.
        co_creators_agreement_accepted: packageId === "menopause_invitation_research",
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout could not be started. Please try again.");
        setBusy(false);
        setBusyPackage(null);
      }
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Checkout could not be started. Please try again."
      );
      setBusy(false);
      setBusyPackage(null);
    }
  };

  return (
    <div data-testid="page-tmi-live" className="bg-cream">
      {/* SOLD OUT BANNER — full-width, top of page. Shown only when the
          entire August 2026 cohort is closed. Preserves the sales page below
          so returning visitors still see what they missed + can join a future
          waitlist through the CTA. */}
      {TMI_LIVE_SOLD_OUT && (
        <div
          className="bg-ink text-cream border-b-4 border-gold"
          data-testid="tmi-live-sold-out-banner"
        >
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start md:items-center gap-4">
              <div className="hidden md:inline-flex w-11 h-11 rounded-full bg-gold/20 items-center justify-center flex-shrink-0">
                <Sparkles size={18} strokeWidth={1.8} className="text-gold" />
              </div>
              <div>
                <div className="eyebrow text-gold mb-1.5">Sold Out · August 2026 Cohort</div>
                <p className="font-serif-display text-lg md:text-xl leading-snug max-w-2xl">
                  The August live cohort of{" "}
                  <em className="italic">The Menopause Invitation — Live</em> has closed.
                  Pip isn&rsquo;t taking any more participants for this round.
                </p>
                <p className="mt-2 text-cream/75 text-sm max-w-2xl leading-relaxed">
                  Explore the on-demand{" "}
                  <em className="italic">Menopause Invitation</em> digital program below, or
                  join the private-sessions waitlist to be first to hear about future live
                  rounds.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 md:ml-6">
              <Link
                to="/the-menopause-invitation"
                className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-ink rounded-full px-5 py-3 font-semibold text-sm tracking-wide transition-all whitespace-nowrap"
                data-testid="tmi-live-sold-out-tmi-cta"
              >
                Explore the Digital Program
                <ArrowRight size={14} strokeWidth={2.2} />
              </Link>
              <Link
                to="/private-sessions"
                className="inline-flex items-center justify-center gap-2 border border-cream/40 hover:border-cream text-cream rounded-full px-5 py-3 font-medium text-sm tracking-wide transition-all whitespace-nowrap"
                data-testid="tmi-live-sold-out-waitlist-cta"
              >
                Private Sessions
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* HERO — focused 4-week live experience messaging. No announcement bar
          above (we removed the 3-pathway umbrella framing). The page now
          positions The Menopause Invitation — Live as a guided 4-week
          experience for women who want to use this season consciously. */}
      <section className="pt-12 md:pt-20 pb-12 md:pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div
              className="eyebrow text-gold-dark mb-4 inline-flex items-center gap-2 max-w-2xl flex-wrap"
              data-testid="tmi-live-eyebrow"
            >
              <Sparkles size={12} strokeWidth={2} />
              A 4-week live experience for women in perimenopause, menopause or post-menopause
            </div>
            <h1
              className="font-serif-display text-4xl md:text-5xl lg:text-[3.5rem] text-ink leading-[1.08] max-w-4xl"
              data-testid="tmi-live-title"
            >
              This is the season where many women stop asking,{" "}
              <em className="italic text-vibrant-dark">&ldquo;How do I keep going?&rdquo;</em>{" "}
              and start asking,{" "}
              <em className="italic text-vibrant-dark">&ldquo;How do I want to live now?&rdquo;</em>
            </h1>

            <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.02rem] md:text-[1.05rem] max-w-2xl">
              <p>
                Menopause can change what you tolerate, what you need, and how you want to live
                from here.
              </p>
              <p>
                <em className="italic text-ink">The Menopause Invitation — Live</em> is a guided
                4-week experience for women who want to use this season consciously — to understand
                what is changing, listen to what can no longer be ignored, and begin shaping the
                next phase of life with more clarity, steadiness and self-trust.
              </p>
              <p>
                You may not be falling apart. You may not even describe yourself as struggling.
                But you may be noticing that the old ways of pushing through, keeping the peace,
                carrying too much, or organising your life around everyone else&apos;s needs no
                longer feel sustainable — or desirable.
              </p>
              <p className="text-ink font-medium">This is not just menopause education.</p>
              <p className="font-serif-display italic text-ink text-lg md:text-xl leading-snug">
                It is a space to ask what you want the next part of your life to be built around.
              </p>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              {TMI_LIVE_SOLD_OUT ? (
                <>
                  <div
                    className="inline-flex items-center justify-center gap-2.5 bg-ink/90 text-cream rounded-full px-9 md:px-10 py-4 md:py-5 font-bold tracking-wide text-base md:text-lg cursor-not-allowed"
                    data-testid="tmi-live-hero-sold-out"
                    aria-disabled="true"
                  >
                    Sold Out — Cohort Closed
                  </div>
                  <Link
                    to="/the-menopause-invitation"
                    className="inline-flex items-center justify-center gap-2 text-vibrant-dark hover:text-vibrant font-semibold text-sm tracking-wide transition"
                    data-testid="tmi-live-hero-alt-cta"
                  >
                    Explore the digital program instead
                    <ArrowRight size={14} strokeWidth={2.2} />
                  </Link>
                </>
              ) : (
                <>
                  <a
                    href="#tmi-live-options"
                    data-testid="tmi-live-hero-cta"
                    className="inline-flex items-center justify-center gap-2.5 bg-vibrant hover:bg-vibrant-dark text-cream rounded-full px-9 md:px-10 py-4 md:py-5 font-bold tracking-wide text-base md:text-lg shadow-[0_18px_45px_-12px_rgba(61,176,122,0.55)] transition-all"
                  >
                    Join Now
                    <ArrowRight size={20} strokeWidth={2.2} />
                  </a>
                  <span className="text-xs text-ink-soft/80">
                    Two ways to join · August 2026 live cohort starts 3 Aug
                  </span>
                </>
              )}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* OPTION CARDS — two ways to join. Live $297 (primary, featured) and
          Co-Creators Circle $40 (gold, requires consent modal before checkout). */}
      <section
        id="tmi-live-options"
        className="py-16 md:py-24 bg-cream-soft border-y border-hairline scroll-mt-24"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Two ways to join</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08] max-w-3xl">
              Choose how you&apos;d like to take part in{" "}
              <em className="italic text-vibrant-dark">The Menopause Invitation — Live</em>.
            </h2>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-ink-soft leading-relaxed">
              Both options give you the full 4-week live cohort experience. The Co-Creators Circle
              offers it at a reduced price in exchange for feedback that helps shape this work.
            </p>
          </SectionReveal>

          <div
            className="mt-12 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
            data-testid="tmi-live-options-grid"
          >
            {/* OPTION 1 — Live Experience $297 */}
            <div
              className={`relative rounded-[1.75rem] border-2 bg-cream p-7 md:p-9 flex flex-col ${
                TMI_LIVE_SOLD_OUT
                  ? "border-hairline opacity-90"
                  : "border-vibrant/50 shadow-[0_22px_55px_-15px_rgba(61,176,122,0.25)] ring-1 ring-vibrant/30"
              }`}
              data-testid="tmi-live-option-live"
            >
              {TMI_LIVE_SOLD_OUT ? (
                <div
                  className="absolute -top-3 left-9 bg-ink text-cream text-[0.65rem] tracking-[0.18em] uppercase font-bold px-3.5 py-1 rounded-full whitespace-nowrap shadow-md"
                  data-testid="tmi-live-option-live-sold-out-badge"
                >
                  Sold Out
                </div>
              ) : (
                <div className="absolute -top-3 left-9 bg-vibrant text-cream text-[0.65rem] tracking-[0.18em] uppercase font-bold px-3.5 py-1 rounded-full whitespace-nowrap">
                  Most chosen
                </div>
              )}
              <div className="text-[0.7rem] tracking-[0.18em] uppercase font-semibold text-vibrant-dark mb-3">
                The full 4-week live experience
              </div>
              <h3 className="font-serif-display text-2xl md:text-[1.85rem] text-ink leading-snug">
                The Menopause Invitation — Live
              </h3>
              <div className="mt-3 font-serif-display text-4xl md:text-5xl text-vibrant-dark">
                $297 <span className="text-xl md:text-2xl text-vibrant-dark/70">AUD</span>
              </div>

              <p className="mt-6 text-ink-soft leading-relaxed text-[0.97rem]">
                For women who want the full live experience, with guidance, reflection,
                conversation and support as they make sense of this season of life and what they
                want the next chapter to be built around.
              </p>
              <p className="mt-4 text-ink-soft leading-relaxed text-[0.97rem]">
                This is for you if menopause has left you noticing that the old ways of pushing
                through, keeping the peace, holding everything together or ignoring your own needs
                no longer work the way they used to.
              </p>

              <p className="mt-7 text-ink font-medium text-sm">You&apos;ll receive:</p>
              <ul className="mt-3 space-y-2.5 text-ink-soft text-[0.93rem] leading-relaxed">
                {[
                  "The full 4-week live cohort experience",
                  "Live Zoom sessions with Pip",
                  "Dashboard and module access",
                  "Workbook access",
                  "Group experience",
                  "Access to Pip during the live experience",
                  "Session recordings where appropriate",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <Check size={13} strokeWidth={2.4} className="shrink-0 mt-1 text-vibrant-dark" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-7 font-serif-display italic text-ink text-[1rem] leading-snug">
                This is not about turning menopause into another self-improvement project. It is
                about understanding this transition with more honesty, compassion and clarity, so
                you can move forward with a stronger sense of what you need now.
              </p>

              <div className="mt-8 flex-1 flex items-end">
                {TMI_LIVE_SOLD_OUT ? (
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    data-testid="tmi-live-option-live-sold-out"
                    className="w-full inline-flex items-center justify-center gap-2.5 bg-ink/85 text-cream/95 rounded-full px-7 py-4 font-bold tracking-wide text-base cursor-not-allowed"
                  >
                    Sold Out — Cohort Closed
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handlePathwayCheckout("menopause_invitation_live")}
                    disabled={busy && busyPackage === "menopause_invitation_live"}
                    data-testid="tmi-live-option-live-cta"
                    className="w-full inline-flex items-center justify-center gap-2.5 bg-vibrant hover:bg-vibrant-dark disabled:opacity-60 text-cream rounded-full px-7 py-4 font-bold tracking-wide text-base shadow-[0_14px_35px_-10px_rgba(61,176,122,0.55)] transition-all"
                  >
                    {busy && busyPackage === "menopause_invitation_live" ? (
                      <>
                        <Loader2 size={16} strokeWidth={1.8} className="animate-spin" />
                        Opening checkout…
                      </>
                    ) : (
                      <>
                        Join Now
                        <ArrowRight size={17} strokeWidth={2.2} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* OPTION 2 — Co-Creators Circle $40 (gold) */}
            <div
              className={`relative rounded-[1.75rem] border-2 bg-cream p-7 md:p-9 flex flex-col ${
                CO_CREATORS_SOLD_OUT
                  ? "border-hairline opacity-90"
                  : "border-gold/50"
              }`}
              data-testid="tmi-live-option-co-creators"
            >
              {CO_CREATORS_SOLD_OUT && (
                <div
                  className="absolute -top-3 left-9 bg-ink text-cream text-[0.65rem] tracking-[0.18em] uppercase font-bold px-3.5 py-1 rounded-full whitespace-nowrap shadow-md"
                  data-testid="tmi-live-co-creators-sold-out-badge"
                >
                  Sold out
                </div>
              )}
              <div className="text-[0.7rem] tracking-[0.18em] uppercase font-semibold text-gold-dark mb-3">
                The full live experience at the co-creator price
              </div>
              <h3 className="font-serif-display text-2xl md:text-[1.85rem] text-ink leading-snug">
                The Menopause Invitation Co-Creators Circle
              </h3>
              <div className="mt-3 font-serif-display text-4xl md:text-5xl text-gold-dark">
                $40 <span className="text-xl md:text-2xl text-gold-dark/70">AUD</span>
              </div>

              <p className="mt-6 text-ink-soft leading-relaxed text-[0.97rem]">
                For women who want the full live experience at the reduced co-creator price and
                are willing to help shape this work through honest feedback.
              </p>

              <p className="mt-6 text-ink font-medium text-sm">
                You&apos;ll receive everything included in The Menopause Invitation — Live:
              </p>
              <ul className="mt-3 space-y-2.5 text-ink-soft text-[0.93rem] leading-relaxed">
                {[
                  "The full 4-week live cohort experience",
                  "Live Zoom sessions with Pip",
                  "Dashboard and module access",
                  "Workbook access",
                  "Group experience",
                  "Access to Pip during the live experience",
                  "Session recordings where appropriate",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <Check size={13} strokeWidth={2.4} className="shrink-0 mt-1 text-gold-dark" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-7 text-ink font-medium text-sm">
                In exchange for the reduced $40 AUD co-creator price, you&apos;ll be asked to
                complete:
              </p>
              <ul className="mt-3 space-y-2.5 text-ink-soft text-[0.93rem] leading-relaxed">
                {[
                  "One feedback form before the program begins",
                  "One feedback form during the program",
                  "One feedback form when the program completes",
                  "One 1:1 conversation with Pip about your experience",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-2.5">
                    <Check size={13} strokeWidth={2.4} className="shrink-0 mt-1 text-gold-dark" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-7 font-serif-display italic text-ink text-[1rem] leading-snug">
                Your feedback will help shape, refine and improve a program designed to support
                women through menopause as a whole-life transition — not just a list of symptoms to
                manage.
              </p>

              <div className="mt-8 flex-1 flex items-end">
                <button
                  type="button"
                  onClick={() => {
                    if (CO_CREATORS_SOLD_OUT) return;
                    setCoCreatorsAgreed(false);
                    setCoCreatorsAgreementOpen(true);
                  }}
                  disabled={CO_CREATORS_SOLD_OUT || (busy && busyPackage === "menopause_invitation_research")}
                  aria-disabled={CO_CREATORS_SOLD_OUT ? "true" : undefined}
                  data-testid="tmi-live-option-co-creators-cta"
                  className={`w-full inline-flex items-center justify-center gap-2.5 rounded-full px-7 py-4 font-bold tracking-wide text-base transition-all ${
                    CO_CREATORS_SOLD_OUT
                      ? "bg-ink-soft/25 text-ink-soft cursor-not-allowed"
                      : "bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink"
                  }`}
                >
                  {CO_CREATORS_SOLD_OUT ? (
                    <>Sold out</>
                  ) : busy && busyPackage === "menopause_invitation_research" ? (
                    <>
                      <Loader2 size={16} strokeWidth={1.8} className="animate-spin" />
                      Opening checkout…
                    </>
                  ) : (
                    <>
                      Join the Co-Creators Circle
                      <ArrowRight size={17} strokeWidth={2.2} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {CO_CREATORS_SOLD_OUT && (
            <div
              className="mt-8 flex items-start gap-3 bg-cream border border-hairline rounded-2xl px-5 py-4 max-w-2xl"
              data-testid="tmi-live-co-creators-sold-out-note"
            >
              <Sparkles size={14} strokeWidth={2.2} className="text-gold-dark flex-shrink-0 mt-0.5" />
              <p className="text-sm text-ink-soft leading-relaxed">
                <strong className="text-ink not-italic">The Co-Creators Circle is now full.</strong>{" "}
                Thank you to the women who&apos;ve stepped in as co-creators — I&apos;m grateful
                for the care you&apos;ll bring to shaping this work. The full{" "}
                <em className="italic">$297 Live experience</em> is still open above if you&apos;d
                like to join the August cohort.
              </p>
            </div>
          )}

          <p className="mt-10 max-w-2xl text-sm text-ink-soft leading-relaxed italic">
            Both options give you full access to The Menopause Invitation — Live. The Co-Creators
            Circle is offered at a reduced price in exchange for the feedback exchange described
            above.
          </p>
        </div>
      </section>

      {/* DIVIDER — everything below is deeper Live-specific copy. The umbrella
          decision has been made above in the pathways tiles; this section onwards
          is for visitors who want to read more about the Live Experience. */}
      <section className="py-12 md:py-16 bg-cream-soft border-y border-hairline" data-testid="tmi-live-deeper-divider">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14 text-center">
          <SectionReveal>
            <div className="eyebrow text-vibrant-dark mb-3">More about the Live Experience</div>
            <h2 className="font-serif-display text-2xl md:text-3xl lg:text-4xl text-ink leading-[1.15] max-w-3xl mx-auto">
              The 4-week live cohort, in depth.
            </h2>
            <p className="mt-5 max-w-2xl mx-auto text-ink-soft leading-relaxed">
              The pages below explore the Live Experience in more detail — what it&apos;s really
              like, the week-by-week curriculum, what other women have said, and the questions
              that come up most often.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* PROSE — "You might not need to be fixed" */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.1]">
              You might not need to be <em className="italic text-vibrant-dark">fixed</em>.
            </h2>
            <p className="mt-6 text-lg md:text-xl text-ink-soft italic leading-relaxed">
              You might need a different way of meeting this season.
            </p>
            <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>Maybe you&apos;ve been told to brace yourself.</p>
              <p>
                Maybe you&apos;ve been offered symptom lists, survival tips, and a lot of noise.
              </p>
              <p>
                Maybe you&apos;ve spent years being capable, reliable, and in control — and now your
                body, mood, energy, sleep, desire, or tolerance for old patterns doesn&apos;t seem
                to be following the same script.
              </p>
              <p>
                That can feel unsettling, because menopause doesn&apos;t just change how things
                feel physically. It can change how you move through your days, how you relate to
                yourself, what you need, what you can no longer ignore, and what you&apos;re
                willing to keep carrying.
              </p>
              <p>
                And if you&apos;re honest, you may already know this isn&apos;t only about hot
                flushes, sleep, or hormones.
              </p>
              <p className="font-serif-display italic text-ink text-xl md:text-2xl leading-snug">
                It&apos;s about the deeper invitation underneath it all.
              </p>
              <p className="font-serif-display italic font-semibold text-ink text-xl md:text-2xl leading-snug">
                And this is where you come to find it.
              </p>
            </div>
            <CTABlock onClick={startCheckout} busy={busy} testId="tmi-live-prose1-cta" />
          </SectionReveal>
        </div>
      </section>

      {/* "FOR YOU IF" */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">For you if</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08] max-w-3xl">
              The Menopause Invitation is for you if:
            </h2>
          </SectionReveal>

          <SectionReveal>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {FOR_YOU.map((item) => (
                <li key={item} className="flex items-start gap-3 text-ink-soft leading-relaxed">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-vibrant/10 text-vibrant-dark shrink-0 mt-0.5">
                    <Check size={13} strokeWidth={2.4} />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          </SectionReveal>

          <SectionReveal>
            <p className="mt-10 max-w-2xl text-ink-soft leading-relaxed">
              This is for the woman who suspects there&apos;s something more in this season than
              she&apos;s been told — and who&apos;s ready to find out what that is.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* WHAT THIS PROGRAM IS */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">What this program is</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              A 4-week live experience — <em className="italic text-vibrant-dark">not</em> a symptom checklist.
            </h2>
            <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>
                The Menopause Invitation — Live is a 4-week live experience that helps you
                explore menopause as a meaningful transition, not just a set of symptoms to
                manage.
              </p>
              <p>
                When you join, you receive access to the full 6-module digital program,{" "}
                <span className="text-ink">plus</span> four weekly live Zoom sessions with Pip.
              </p>
              <p>The digital program gives you the framework.</p>
              <p>
                The live sessions give you the space to actually use it — with teaching, real-time
                reflection, and coaching from Pip.
              </p>
              <p className="text-ink font-medium">Together, they are designed to help you:</p>
            </div>
            <ul className="mt-6 space-y-3">
              {[
                "Understand what&apos;s happening beneath the surface",
                "Make sense of your experience without overcomplicating it",
                "Notice the patterns, beliefs, and pressures that may be amplifying the struggle",
                "Reconnect with your own steadiness",
                "Take practical next steps that feel aligned",
                "Begin trusting yourself in this season — rather than fighting yourself through it",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-ink-soft leading-relaxed">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-vibrant/10 text-vibrant-dark shrink-0 mt-0.5">
                    <Check size={13} strokeWidth={2.4} />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
            <div className="mt-10 space-y-4 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>This is not about perfection.</p>
              <p>It&apos;s not about performing wellness.</p>
              <p>And it&apos;s not about pretending this stage is easy when it isn&apos;t.</p>
              <p className="font-serif-display italic text-ink text-xl md:text-2xl leading-snug">
                It&apos;s about getting real — so you can meet it with more self-trust.
              </p>
            </div>
            <CTABlock onClick={startCheckout} busy={busy} testId="tmi-live-program-cta" />
          </SectionReveal>
        </div>
      </section>

      {/* WHAT CHANGES */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">What changes</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              When you stop fighting the <em className="italic text-vibrant-dark">transition</em>.
            </h2>
            <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>
                When menopause is framed only as a problem, it can make everything feel smaller.
              </p>
              <p>You start asking, &ldquo;What&apos;s wrong with me?&rdquo;</p>
              <p>You start narrowing your life around what feels harder.</p>
              <p>
                You start assuming the answer is to endure, manage, or push through.
              </p>
              <p>
                But when you understand the transition more clearly — something else becomes possible.
              </p>
            </div>
            <ul className="mt-8 space-y-3">
              {[
                "You can make decisions with less noise.",
                "You can stop outsourcing your authority.",
                "You can respond instead of react.",
                "You can feel more anchored in what matters now.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-ink leading-relaxed">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-vibrant/10 text-vibrant-dark shrink-0 mt-0.5">
                    <Check size={13} strokeWidth={2.4} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-ink-soft leading-relaxed">
              Not because menopause magically solves anything.{" "}
              <span className="text-ink font-medium">Because clarity changes how you move.</span>
            </p>
            <p className="mt-4 font-serif-display italic text-ink text-lg md:text-xl leading-snug">
              And you deserve to move from a place that feels like you.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* TESTIMONIALS — pull quotes from past clients */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-vibrant-dark mb-3">Real stories</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08] max-w-3xl">
              Women who&apos;ve <em className="italic text-vibrant-dark">been here</em>.
            </h2>
          </SectionReveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <SectionReveal key={t.name} delay={i * 70}>
                <figure className="h-full rounded-[1.5rem] border border-hairline bg-cream p-7 md:p-8 flex flex-col">
                  <blockquote className="font-serif-display text-lg md:text-[1.2rem] text-ink leading-snug italic flex-1">
                    {t.body}
                  </blockquote>
                  <figcaption className="mt-6 text-sm text-ink-soft tracking-wide">
                    — <span className="text-vibrant-dark font-medium">{t.name}</span>,{" "}
                    {t.location}
                  </figcaption>
                </figure>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">What&apos;s included</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08] max-w-3xl">
              Everything you need to <em className="italic text-vibrant-dark">actually move</em> through this.
            </h2>
            <p className="mt-4 font-serif-display italic text-base md:text-lg text-ink-soft leading-relaxed max-w-2xl">
              $297 AUD — less than the cost of a single private coaching session with Pip.
            </p>
          </SectionReveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {INCLUSIONS.map((item, i) => {
              const Icon = item.icon;
              return (
                <SectionReveal key={item.title} delay={i * 70}>
                  <div className="rounded-[1.5rem] border border-hairline bg-cream p-7 md:p-8 h-full">
                    <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-vibrant/10 text-vibrant-dark mb-5">
                      <Icon size={18} strokeWidth={1.8} />
                    </div>
                    <h3 className="font-serif-display text-xl md:text-2xl text-ink leading-snug">
                      {item.title}
                    </h3>
                    <p
                      className="mt-3 text-ink-soft leading-relaxed text-[0.95rem]"
                      dangerouslySetInnerHTML={{ __html: item.body }}
                    />
                  </div>
                </SectionReveal>
              );
            })}
          </div>

          <SectionReveal>
            <CTABlock onClick={startCheckout} busy={busy} testId="tmi-live-included-cta" />
          </SectionReveal>
        </div>
      </section>

      {/* CURRICULUM — Inside the live 4 weeks */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Inside the live 4 weeks</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08] max-w-3xl">
              Each week builds on the last — so you&apos;re not just learning, you&apos;re applying.
            </h2>
          </SectionReveal>

          <div className="mt-12 space-y-5">
            {CURRICULUM.map((w, i) => (
              <SectionReveal key={w.week} delay={i * 60}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-8 rounded-[1.5rem] border border-hairline bg-cream-soft p-7 md:p-9">
                  <div className="md:col-span-3">
                    <div className="eyebrow text-vibrant-dark mb-2">{w.week}</div>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-serif-display text-xl md:text-2xl text-ink leading-snug">
                      {w.title}
                    </h3>
                    <p
                      className="mt-3 text-ink-soft leading-relaxed text-[0.97rem]"
                      dangerouslySetInnerHTML={{ __html: w.body }}
                    />
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>

          <SectionReveal>
            <p className="mt-12 max-w-2xl text-ink-soft leading-relaxed">
              You&apos;ll leave with more than insight. You&apos;ll leave with a way of thinking and
              responding that feels steadier.
            </p>
            <CTABlock onClick={startCheckout} busy={busy} testId="tmi-live-curriculum-cta" />
          </SectionReveal>
        </div>
      </section>

      {/* LIVE SESSION DATES */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Live session dates</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08] max-w-3xl">
              Four Monday evenings. <em className="italic text-vibrant-dark">Seven pm AEST.</em>
            </h2>
            <p className="mt-5 max-w-2xl text-base md:text-lg text-ink-soft leading-relaxed">
              Live on Zoom. Each session includes teaching, reflection, and live Q&A/coaching.
              The teaching portion is recorded and made available after each session. The live
              coaching and Q&A portion is not recorded.
            </p>
          </SectionReveal>

          <SectionReveal>
            <ul className="mt-10 space-y-3 max-w-2xl">
              {COHORT_DATES.map((d, i) => (
                <li
                  key={d}
                  className="flex items-center gap-4 rounded-2xl border border-hairline bg-cream px-5 py-4"
                >
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-vibrant text-cream text-sm font-semibold">
                    {i + 1}
                  </span>
                  <span className="text-ink font-medium text-base md:text-lg">{d}</span>
                </li>
              ))}
            </ul>
          </SectionReveal>

          <SectionReveal>
            <p className="mt-10 max-w-2xl text-base md:text-lg text-ink leading-relaxed">
              This is a rare chance to move through this season in real time — with other women,
              with Pip live in the room, and with a framework that actually honours the depth of
              what&apos;s happening.{" "}
              <span className="font-serif-display italic text-vibrant-dark">It won&apos;t run again in 2026.</span>
            </p>
            <CTABlock onClick={startCheckout} busy={busy} testId="tmi-live-dates-cta" />
          </SectionReveal>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Why this matters</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              A chance to stop treating your own experience as an <em className="italic text-vibrant-dark">inconvenience</em>.
            </h2>
            <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>
                Menopause can become a point where many women quietly begin to doubt themselves.
              </p>
              <p>Not because they&apos;re weak.</p>
              <p>
                Because they&apos;ve spent years adjusting, accommodating, managing, and holding
                everything together.
              </p>
              <p>
                This program is a chance to stop treating your own experience as an inconvenience.
              </p>
              <p>It&apos;s a chance to meet yourself with more honesty.</p>
              <p>
                And it&apos;s a chance to notice that this season may be less about loss than it
                first appears.
              </p>
              <p className="font-serif-display italic text-ink text-xl md:text-2xl leading-snug">
                There is a real invitation here.
              </p>
              <p>
                The question is whether you want to keep skimming over it — or meet it properly.
              </p>
            </div>
            <p className="mt-10 text-ink font-medium">You&apos;ll get:</p>
            <ul className="mt-5 space-y-3">
              {YOULL_GET.map((item) => (
                <li key={item} className="flex items-start gap-3 text-ink-soft leading-relaxed">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-vibrant/10 text-vibrant-dark shrink-0 mt-0.5">
                    <Check size={13} strokeWidth={2.4} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-ink-soft leading-relaxed">
              If you can&apos;t attend every session live, you can still do the work. You&apos;ll
              have access to the digital program and the teaching recordings, and you can
              continue applying the material in your own time.
            </p>
            <CTABlock onClick={startCheckout} busy={busy} testId="tmi-live-why-cta" />
          </SectionReveal>
        </div>
      </section>

      {/* NOT FOR YOU */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-ink-soft mb-3">A note on fit</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              This is <em className="italic text-vibrant-dark">not</em> for you if:
            </h2>
            <ul className="mt-10 space-y-3">
              {NOT_FOR_YOU.map((item) => (
                <li key={item} className="flex items-start gap-3 text-ink-soft leading-relaxed">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-ink-soft/10 text-ink-soft shrink-0 mt-0.5">
                    <X size={13} strokeWidth={2.4} />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
            <p className="mt-10 font-serif-display italic text-ink text-xl md:text-2xl leading-snug">
              This work is gentle — but it isn&apos;t vague.
            </p>
            <p className="mt-3 text-ink-soft leading-relaxed">It asks for your attention.</p>
          </SectionReveal>
        </div>
      </section>

      {/* WHY WORK WITH ME */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Why work with me</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              Menopause isn&apos;t only experienced in the <em className="italic text-vibrant-dark">body</em>.
            </h2>
            <div className="mt-8 space-y-5 text-ink-soft leading-relaxed text-[1.02rem]">
              <p>
                I bring together menopause education, strategic life coaching, positive psychology
                coaching, coaching for transitions, and clinical hypnotherapy — because menopause
                isn&apos;t only experienced in the body.
              </p>
              <p>
                It&apos;s also experienced through habits, beliefs, pressure, identity,
                relationships, and the way you&apos;ve learned to cope.
              </p>
              <p>
                As a{" "}
                <span className="text-ink font-medium">Certified Menopause Coach</span>, I
                understand the physical and hormonal realities of this stage.
              </p>
              <p>
                As a coach specialising in Positive Psychology Coaching and Coaching for
                Transitions, I also understand that menopause often asks deeper questions about
                identity, meaning, resilience, relationships, self-trust, and the life you are
                ready to stop performing.
              </p>
              <p className="text-ink">That means the work needs to be thoughtful.</p>
              <p>Not dramatic.</p>
              <p>Not fluffy.</p>
              <p>Not reduced to a list of symptoms.</p>
              <p>
                And not so clinical that the woman inside the transition disappears.
              </p>
              <p className="font-serif-display italic text-ink text-xl md:text-2xl leading-snug">
                Just clear, grounded support for a season that deserves more than noise.
              </p>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* OPTIONAL ADD-ONS */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Optional add-ons</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              Want to go <em className="italic text-vibrant-dark">deeper</em>?
            </h2>
            <p className="mt-6 text-ink-soft leading-relaxed">
              There will also be optional add-ons available as separate purchases:
            </p>
            <ul className="mt-6 space-y-3">
              <li className="flex items-start gap-3 text-ink-soft leading-relaxed">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gold/15 text-gold-dark shrink-0 mt-0.5">
                  <Sparkles size={12} strokeWidth={2.2} />
                </span>
                <span>A couples / partner Zoom session</span>
              </li>
              <li className="flex items-start gap-3 text-ink-soft leading-relaxed">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gold/15 text-gold-dark shrink-0 mt-0.5">
                  <Sparkles size={12} strokeWidth={2.2} />
                </span>
                <span>2 × 1:1 sessions with Pip</span>
              </li>
            </ul>
            <p className="mt-6 text-sm text-ink-soft/80 italic">
              Details and pricing will be confirmed separately.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-cream-soft border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Frequently asked</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              The questions that <em className="italic text-vibrant-dark">come up</em>.
            </h2>
          </SectionReveal>

          <div className="mt-10 divide-y divide-hairline border-y border-hairline">
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem key={item.q} q={item.q} a={item.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-14 text-center">
          <SectionReveal>
            <div className="eyebrow text-gold-dark mb-3">Ready to begin?</div>
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.08]">
              Meet this season <em className="italic text-vibrant-dark">properly</em>.
            </h2>
            <p className="mt-6 max-w-xl mx-auto text-ink-soft leading-relaxed">
              The Menopause Invitation — Live starts Monday 3 August 2026 at 7pm AEST.
            </p>
            <ul className="mt-6 space-y-1 text-ink-soft leading-relaxed">
              <li>Four weeks.</li>
              <li>Real-time support.</li>
              <li>Clear, grounded guidance.</li>
              <li>A different way of meeting this season.</li>
            </ul>
            <p className="mt-6 text-sm italic text-ink-soft/80">
              2026 only — this live round won&apos;t run again this year.
            </p>
            <div className="mt-10 flex flex-col items-center gap-3">
              {TMI_LIVE_SOLD_OUT ? (
                <div className="flex flex-col items-center gap-4">
                  <div
                    className="inline-flex items-center justify-center gap-2 bg-ink text-cream rounded-full px-8 py-4 font-bold tracking-wide text-base cursor-not-allowed"
                    data-testid="tmi-live-final-sold-out"
                    aria-disabled="true"
                  >
                    Sold Out — Cohort Closed
                  </div>
                  <Link
                    to="/the-menopause-invitation"
                    className="inline-flex items-center gap-2 text-vibrant-dark hover:text-vibrant font-semibold text-sm tracking-wide transition"
                    data-testid="tmi-live-final-alt-cta"
                  >
                    Explore the digital program instead
                    <ArrowRight size={14} strokeWidth={2.2} />
                  </Link>
                </div>
              ) : (
                <CTAButton onClick={startCheckout} busy={busy} testId="tmi-live-final-cta" size="lg" />
              )}
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* Floating sticky CTA — hidden when the cohort is sold out (no reason
          to nudge visitors toward a closed checkout). */}
      {!TMI_LIVE_SOLD_OUT && (
        <StickyCTA
          label="I Accept My Invitation"
          href="#tmi-live-options"
          tone="vibrant"
          showAfter={0}
          testId="sticky-cta-tmi-live"
        />
      )}

      {/* Co-Creators Circle agreement modal — required gate before $40 checkout.
          We don't open Stripe until the visitor explicitly ticks the consent
          checkbox. The same modal is used regardless of whether the click came
          from the tile face or the flipped "What's included" view. */}
      {coCreatorsAgreementOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-ink/60 backdrop-blur-sm overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="co-creators-agreement-title"
          data-testid="co-creators-agreement-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !(busy && busyPackage === "menopause_invitation_research")) {
              setCoCreatorsAgreementOpen(false);
            }
          }}
        >
          <div className="relative bg-cream rounded-3xl max-w-xl w-full p-7 md:p-9 border-2 border-gold/40 shadow-[0_30px_80px_-15px_rgba(0,0,0,0.35)] my-auto">
            <button
              type="button"
              onClick={() => setCoCreatorsAgreementOpen(false)}
              className="absolute top-5 right-5 text-ink-soft hover:text-ink transition"
              aria-label="Close"
              data-testid="co-creators-agreement-close"
              disabled={busy && busyPackage === "menopause_invitation_research"}
            >
              <X size={20} strokeWidth={1.8} />
            </button>

            <div className="text-[0.7rem] tracking-[0.18em] uppercase font-semibold text-gold-dark mb-3">
              Before you continue
            </div>
            <h2
              id="co-creators-agreement-title"
              className="font-serif-display text-2xl md:text-3xl text-ink leading-snug mb-5"
            >
              Co-Creators Circle agreement
            </h2>

            <div className="text-ink-soft leading-relaxed text-[0.95rem] space-y-4 max-h-[40vh] overflow-y-auto pr-1">
              <p>
                I understand I am joining The Menopause Invitation Co-Creators Circle at a reduced
                co-creator price of <strong className="text-ink">$40 AUD</strong> in exchange for
                completing three feedback forms.
              </p>
              <p>
                I understand my feedback may be used to shape, improve and promote the program,
                and that any public use of my feedback will be <strong className="text-ink">de-identified</strong> unless
                I give additional permission for my name, image or identifying details to be used.
              </p>
            </div>

            <label
              className="mt-6 flex items-start gap-3 cursor-pointer select-none bg-gold/10 border border-gold/40 rounded-2xl p-4 hover:bg-gold/15 transition"
              data-testid="co-creators-agreement-label"
            >
              <input
                type="checkbox"
                checked={coCreatorsAgreed}
                onChange={(e) => setCoCreatorsAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 accent-gold-dark flex-shrink-0"
                data-testid="co-creators-agreement-checkbox"
              />
              <span className="text-sm text-ink leading-relaxed">
                I have read and agree to the Co-Creators Circle terms above.
              </span>
            </label>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setCoCreatorsAgreementOpen(false)}
                disabled={busy && busyPackage === "menopause_invitation_research"}
                className="flex-1 inline-flex items-center justify-center rounded-full px-5 py-3 text-sm font-medium text-ink-soft hover:text-ink border border-hairline hover:border-ink-soft transition disabled:opacity-50"
                data-testid="co-creators-agreement-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handlePathwayCheckout("menopause_invitation_research")}
                disabled={!coCreatorsAgreed || (busy && busyPackage === "menopause_invitation_research")}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm md:text-[0.95rem] font-semibold bg-gold hover:bg-gold-dark text-ink disabled:opacity-40 disabled:cursor-not-allowed transition"
                data-testid="co-creators-agreement-confirm"
              >
                {busy && busyPackage === "menopause_invitation_research" ? (
                  <>
                    <Loader2 size={15} strokeWidth={1.8} className="animate-spin" />
                    Opening checkout…
                  </>
                ) : (
                  <>
                    Continue to checkout
                    <ArrowRight size={15} strokeWidth={2.2} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
